import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { appLogger } from '@/domain/services/analytics/appLogger';

/**
 * TriggerLastSeenTimestampUseCase
 *
 * Reports the current user's "last seen" timestamp to the backend via the
 * `setLastSeenTimestamp` Cloud Function. Web port of Flutter
 * `CloudFunctionsService.triggerLastSeenTimestamp` (lib/services/cloud_functions_service.dart:983).
 *
 * `platform` is the host-platform code stored verbatim on the user doc. Flutter
 * sends the `TargetPlatform` ordinal; web has no faithful equivalent and sends
 * the server's "unspecified platform" sentinel `-1` (`COMMON_CONSTANTS.webPlatformCode`). The
 * server itself defaults absent values to `-1` (set_last_seen_timestamp.ts:16).
 *
 * No return value — fire-and-forget. The data service already swallows and logs
 * errors, but `execute` guards the rejection at the use-case boundary too so the
 * listener's unawaited call (`LastSeenTimestampListener.tsx`) can never surface
 * an unhandled promise rejection during a tab-hidden / unload transition, even if
 * the data service's contract ever changes to propagate a rejection.
 */
export class TriggerLastSeenTimestampUseCase {
    async execute(platform: number): Promise<void> {
        try {
            await cloudFunctionsService.triggerLastSeenTimestamp(platform);
        } catch (error) {
            appLogger.error(
                'Error triggering last seen timestamp',
                'TriggerLastSeenTimestampUseCase.execute',
                { error: error as Error },
            );
        }
    }
}

export const triggerLastSeenTimestampUseCase = new TriggerLastSeenTimestampUseCase();
